import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Plus, Settings, Trash2, Check, AlertCircle, Loader2, Mail, Lock, Octagon, Bell } from 'lucide-react';

interface SMTPConfig {
  id: string;
  provider: string;
  transportType?: 'smtp' | 'api';
  fromName: string;
  fromEmail: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  apiKey?: string;
  isActive: boolean;
  createdAt?: string;
}

const DEFAULT_SMTP_CONFIG: Omit<SMTPConfig, 'id'> = {
  provider: '',
  transportType: 'smtp',
  fromName: '',
  fromEmail: '',
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  apiKey: '',
  isActive: false
};

const COMMON_PROVIDERS = [
  {
    name: 'Gmail',
    transportType: 'smtp' as const,
    host: 'smtp.gmail.com',
    port: 587,
    secure: false
  },
  {
    name: 'Outlook',
    transportType: 'smtp' as const,
    host: 'smtp-mail.outlook.com',
    port: 587,
    secure: false
  },
  {
    name: 'Yahoo',
    transportType: 'smtp' as const,
    host: 'smtp.mail.yahoo.com',
    port: 587,
    secure: false
  },
  {
    name: 'SendGrid',
    transportType: 'smtp' as const,
    host: 'smtp.sendgrid.net',
    port: 587,
    secure: false
  },
  {
    name: 'Resend',
    transportType: 'api' as const,
    host: '',
    port: 443,
    secure: true
  }
];

export const SMTPConfiguration: React.FC = () => {
  // The /api/email routes are admin-gated; every call must carry the bearer token.
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const [configurations, setConfigurations] = useState<SMTPConfig[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SMTPConfig | null>(null);
  const [formData, setFormData] = useState<Omit<SMTPConfig, 'id'>>(DEFAULT_SMTP_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testConfigId, setTestConfigId] = useState<string>('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [emailsEnabled, setEmailsEnabled] = useState(false);
  const [envOverride, setEnvOverride] = useState(false);
  const [isTogglingEmail, setIsTogglingEmail] = useState(false);
  const [workflows, setWorkflows] = useState<{ key: string; label: string; description: string; enabled: boolean }[]>([]);
  const [isTogglingWorkflow, setIsTogglingWorkflow] = useState<string | null>(null);

  // Load configurations and email status on mount
  useEffect(() => {
    loadConfigurations();
    loadEmailStatus();
    loadWorkflowDefaults();
  }, []);

  const loadConfigurations = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/email/configurations', { headers: getAuthHeaders() });
      const result = await response.json();
      
      if (result.success) {
        setConfigurations(result.data || []);
      } else {
        console.error('Failed to load SMTP configurations:', result.message);
      }
    } catch (error) {
      console.error('Error loading SMTP configurations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmailStatus = async () => {
    try {
      const response = await fetch('/api/email/status', { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) {
        setEmailsEnabled(result.data.emailsEnabled);
        setEnvOverride(result.data.envOverride);
      }
    } catch (error) {
      console.error('Error loading email status:', error);
    }
  };

  const loadWorkflowDefaults = async () => {
    try {
      const response = await fetch('/api/email/workflow-defaults', { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) {
        setWorkflows(result.data.registry);
      }
    } catch (error) {
      console.error('Error loading workflow defaults:', error);
    }
  };

  const handleToggleWorkflow = async (workflowKey: string, currentValue: boolean) => {
    setIsTogglingWorkflow(workflowKey);
    // Optimistic update
    setWorkflows(prev => prev.map(w => w.key === workflowKey ? { ...w, enabled: !currentValue } : w));
    try {
      // Build full defaults object from current state
      const defaults: Record<string, boolean> = {};
      workflows.forEach(w => {
        defaults[w.key] = w.key === workflowKey ? !currentValue : w.enabled;
      });
      const response = await fetch('/api/email/workflow-defaults', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(defaults)
      });
      const result = await response.json();
      if (!result.success) {
        // Revert on failure
        setWorkflows(prev => prev.map(w => w.key === workflowKey ? { ...w, enabled: currentValue } : w));
      }
    } catch (error) {
      console.error('Error toggling workflow:', error);
      setWorkflows(prev => prev.map(w => w.key === workflowKey ? { ...w, enabled: currentValue } : w));
    } finally {
      setIsTogglingWorkflow(null);
    }
  };

  const handleToggleEmail = async () => {
    if (envOverride || isTogglingEmail) return;
    const newValue = !emailsEnabled;
    setIsTogglingEmail(true);
    try {
      const response = await fetch('/api/email/status', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ emailsEnabled: newValue })
      });
      const result = await response.json();
      if (result.success) {
        setEmailsEnabled(newValue);
      }
    } catch (error) {
      console.error('Error toggling email status:', error);
    } finally {
      setIsTogglingEmail(false);
    }
  };

  const saveConfigurations = async (updatedConfigurations: SMTPConfig[]) => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/email/configurations', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(updatedConfigurations)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setHasUnsavedChanges(false);
        return true;
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error saving SMTP configurations:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderSelect = (provider: typeof COMMON_PROVIDERS[0]) => {
    if (provider.transportType === 'api') {
      setFormData(prev => ({
        ...prev,
        provider: provider.name,
        transportType: 'api',
        host: '',
        port: 443,
        secure: true,
        username: '',
        password: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        provider: provider.name,
        transportType: 'smtp',
        host: provider.host,
        port: provider.port,
        secure: provider.secure,
        apiKey: ''
      }));
    }
    setHasUnsavedChanges(true);
  };

  const handleInputChange = (field: keyof Omit<SMTPConfig, 'id'>, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddConfig = () => {
    setEditingConfig(null);
    setFormData(DEFAULT_SMTP_CONFIG);
    setTestResult(null);
    setIsModalOpen(true);
  };

  const handleEditConfig = (config: SMTPConfig) => {
    setEditingConfig(config);
    setFormData({ ...config });
    setTestResult(null);
    setIsModalOpen(true);
  };

  const handleSaveConfig = async () => {
    const configId = editingConfig?.id || `smtp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newConfig: SMTPConfig = {
      ...formData,
      id: configId,
      createdAt: editingConfig?.createdAt || new Date().toISOString()
    };

    let updatedConfigurations;
    if (editingConfig) {
      // Update existing
      updatedConfigurations = configurations.map(config => 
        config.id === editingConfig.id ? newConfig : config
      );
    } else {
      // Add new
      updatedConfigurations = [...configurations, newConfig];
    }

    // Update local state and save
    setConfigurations(updatedConfigurations);
    const saved = await saveConfigurations(updatedConfigurations);
    
    if (saved) {
      setIsModalOpen(false);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    const updatedConfigurations = configurations.filter(config => config.id !== configId);
    setConfigurations(updatedConfigurations);
    await saveConfigurations(updatedConfigurations);
  };

  const handleSetActive = async (configId: string) => {
    const updatedConfigurations = configurations.map(config => ({
      ...config,
      isActive: config.id === configId
    }));

    setConfigurations(updatedConfigurations);
    await saveConfigurations(updatedConfigurations);
  };

  const handleDeactivate = async (configId: string) => {
    const updatedConfigurations = configurations.map(config => ({
      ...config,
      isActive: config.id === configId ? false : config.isActive
    }));

    setConfigurations(updatedConfigurations);
    await saveConfigurations(updatedConfigurations);
  };

  const handleOpenTestModal = (configId: string) => {
    setTestConfigId(configId);
    setTestEmailAddress('');
    setTestResult(null);
    setIsTestModalOpen(true);
  };

  const handleCloseTestModal = () => {
    setIsTestModalOpen(false);
    setTestConfigId('');
    setTestEmailAddress('');
    setTestResult(null);
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !testConfigId) return;

    try {
      setIsTesting(true);
      setTestResult(null);
      
      const response = await fetch('/api/email/test', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          configId: testConfigId,
          testEmail: testEmailAddress
        })
      });
      
      const result = await response.json();
      setTestResult({
        success: result.success,
        message: result.message
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Test failed: Network error'
      });
    } finally {
      setIsTesting(false);
    }
  };

  const isApiTransport = formData.transportType === 'api';

  const renderConfigForm = () => (
    <div className="smtp-form">
      {/* Provider Quick Select */}
      <div className="form-section">
        <label className="form-label">Quick Setup</label>
        <div className="provider-buttons">
          {COMMON_PROVIDERS.map(provider => (
            <button
              key={provider.name}
              type="button"
              onClick={() => handleProviderSelect(provider)}
              className={`provider-button ${formData.provider === provider.name ? 'active' : ''}`}
            >
              {provider.name}
            </button>
          ))}
        </div>
      </div>

      {/* Provider Setup Guide */}
      {formData.provider === 'Resend' && (
        <div className="provider-guide">
          <h4>Resend Setup</h4>
          <ol>
            <li>Create a free account at <strong>resend.com</strong> (3,000 emails/month)</li>
            <li>Add and verify your sending domain under <strong>Domains</strong></li>
            <li>Generate an API key under <strong>API Keys</strong></li>
            <li>Set <strong>From Email</strong> below to an address on your verified domain</li>
          </ol>
        </div>
      )}
      {formData.provider === 'Gmail' && (
        <div className="provider-guide">
          <h4>Gmail Setup</h4>
          <ol>
            <li>Enable 2-Factor Authentication on your Google account</li>
            <li>Go to <strong>Google Account &rarr; Security &rarr; App passwords</strong></li>
            <li>Generate an app password and use it as the password below</li>
            <li>Regular Gmail passwords will not work — you must use an app password</li>
          </ol>
        </div>
      )}
      {formData.provider === 'SendGrid' && (
        <div className="provider-guide">
          <h4>SendGrid Setup</h4>
          <ol>
            <li>Create a SendGrid account and complete sender authentication</li>
            <li>Generate an API key under <strong>Settings &rarr; API Keys</strong></li>
            <li>Use <strong>apikey</strong> as the username and your API key as the password</li>
          </ol>
        </div>
      )}

      {/* Basic Settings */}
      <div className="form-section">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Provider Name</label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => handleInputChange('provider', e.target.value)}
              placeholder="e.g. Gmail, Resend"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label className="form-label">From Name</label>
            <input
              type="text"
              value={formData.fromName}
              onChange={(e) => handleInputChange('fromName', e.target.value)}
              placeholder="Mission: Trimpossible"
              className="form-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">From Email</label>
          <input
            type="email"
            value={formData.fromEmail}
            onChange={(e) => handleInputChange('fromEmail', e.target.value)}
            placeholder="noreply@missiontrimpossible.com"
            className="form-input"
          />
        </div>
      </div>

      {/* API Settings (Resend) */}
      {isApiTransport && (
        <div className="form-section">
          <h3>API Settings</h3>
          <div className="form-group">
            <label className="form-label">API Key</label>
            <input
              type="password"
              value={formData.apiKey || ''}
              onChange={(e) => handleInputChange('apiKey', e.target.value)}
              placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="form-input"
            />
          </div>
          <p className="form-hint">Emails are sent over HTTPS (port 443) — no SMTP ports required.</p>
        </div>
      )}

      {/* SMTP Settings */}
      {!isApiTransport && (
        <div className="form-section">
          <h3>SMTP Settings</h3>
          <div className="form-row">
            <div className="form-group flex-2">
              <label className="form-label">SMTP Host</label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                placeholder="smtp.gmail.com"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Port</label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => handleInputChange('port', parseInt(e.target.value))}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="your-email@gmail.com"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Your app password"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.secure}
                onChange={(e) => handleInputChange('secure', e.target.checked)}
              />
              Use SSL/TLS (recommended for port 465)
            </label>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="smtp-configuration">
      <div className="page-header">
        <h2>Email Configuration</h2>
        <p>Manage email delivery settings and test connectivity</p>
      </div>

      <div className="email-toggle-section">
        <div className="email-toggle-row">
          <div className="email-toggle-info">
            <span className="email-toggle-label">Email Sending</span>
            <span className={`email-toggle-status ${emailsEnabled && !envOverride ? 'enabled' : 'disabled'}`}>
              {emailsEnabled && !envOverride ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="email-toggle-control">
            {envOverride && (
              <span className="env-override-badge">
                <Lock size={14} />
                Locked by environment variable
              </span>
            )}
            <button
              type="button"
              role="switch"
              aria-checked={emailsEnabled}
              onClick={handleToggleEmail}
              disabled={envOverride || isTogglingEmail}
              className={`toggle-switch ${emailsEnabled && !envOverride ? 'on' : 'off'} ${envOverride ? 'locked' : ''}`}
            >
              <span className="toggle-knob" />
            </button>
          </div>
        </div>
      </div>

      {/* Workflow Defaults Section */}
      <div className="workflow-defaults-section">
        <div className="workflow-header">
          <Bell size={20} />
          <div>
            <h3>Notification Workflows</h3>
            <p>Control which automated emails are sent platform-wide. Coaches can override per client.</p>
          </div>
        </div>
        {workflows.length === 0 ? (
          <div className="workflow-loading">Loading workflows...</div>
        ) : (
          <div className="workflow-list">
            {workflows.map(workflow => (
              <div key={workflow.key} className="workflow-item">
                <div className="workflow-info">
                  <span className="workflow-label">{workflow.label}</span>
                  <span className="workflow-description">{workflow.description}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={workflow.enabled}
                  onClick={() => handleToggleWorkflow(workflow.key, workflow.enabled)}
                  disabled={isTogglingWorkflow === workflow.key || !emailsEnabled || envOverride}
                  className={`toggle-switch ${workflow.enabled ? 'on' : 'off'} ${!emailsEnabled || envOverride ? 'locked' : ''}`}
                  title={!emailsEnabled ? 'Enable email sending first' : undefined}
                >
                  <span className="toggle-knob" />
                </button>
              </div>
            ))}
          </div>
        )}
        {!emailsEnabled && workflows.length > 0 && (
          <p className="workflow-disabled-hint">Enable email sending above to manage workflow defaults.</p>
        )}
      </div>

      <div className="configuration-controls">
        <button
          onClick={handleAddConfig}
          className="add-config-button"
        >
          <Plus size={20} />
          Add Email Configuration
        </button>
      </div>

      {/* Configuration List */}
      <div className="configurations-list">
        {isLoading && configurations.length === 0 ? (
          <div className="loading-state">
            <Loader2 size={24} className="animate-spin" />
            Loading configurations...
          </div>
        ) : configurations.length === 0 ? (
          <div className="empty-state">
            <Settings size={48} />
            <h3>No Email Configurations</h3>
            <p>Add an email configuration to enable email delivery</p>
          </div>
        ) : (
          configurations.map(config => (
            <div key={config.id} className={`config-card ${config.isActive ? 'active' : ''}`}>
              <div className="config-header">
                <div className="config-info">
                  <h3>
                    {config.provider || config.host}
                    {config.isActive && <span className="active-badge">Active</span>}
                  </h3>
                  <p>{config.fromName} &lt;{config.fromEmail}&gt;</p>
                  <p className="config-details">
                    {config.transportType === 'api'
                      ? 'HTTPS API transport'
                      : `${config.host}:${config.port} ${config.secure ? '(SSL)' : '(STARTTLS)'}`}
                  </p>
                </div>
                
                <div className="config-actions">
                  {config.isActive ? (
                    <button
                      onClick={() => handleDeactivate(config.id)}
                      className="action-button deactivate"
                      title="Deactivate configuration"
                    >
                      <Octagon size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSetActive(config.id)}
                      className="action-button activate"
                      title="Set as active"
                    >
                      <Check size={16} />
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleOpenTestModal(config.id)}
                    className="action-button test"
                    title="Test configuration"
                  >
                    <Mail size={16} />
                  </button>
                  
                  <button
                    onClick={() => handleEditConfig(config)}
                    className="action-button edit"
                    title="Edit configuration"
                  >
                    <Settings size={16} />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteConfig(config.id)}
                    className="action-button delete"
                    title="Delete configuration"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Configuration Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingConfig ? 'Edit Email Configuration' : 'Add Email Configuration'}
        hasUnsavedChanges={hasUnsavedChanges}
        size="large"
      >
        {renderConfigForm()}
        
        <div className="modal-actions">
          <button 
            onClick={() => setIsModalOpen(false)}
            className="cancel-button"
          >
            Cancel
          </button>
          <button 
            onClick={handleSaveConfig}
            className="save-button"
            disabled={!formData.provider || !formData.fromEmail || (isApiTransport ? !formData.apiKey : !formData.host) || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              editingConfig ? 'Update Configuration' : 'Add Configuration'
            )}
          </button>
        </div>
      </Modal>

      {/* Test Email Modal */}
      <Modal
        isOpen={isTestModalOpen}
        onClose={handleCloseTestModal}
        title="Test Email Configuration"
        size="medium"
      >
        <div className="test-modal-content">
          <p>Send a test email to verify this SMTP configuration is working correctly.</p>
          
          <div className="form-group">
            <label>Test Email Address</label>
            <input
              type="email"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
              placeholder="Enter email address to test"
              className="form-input"
              required
            />
          </div>

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              <div className="result-icon">
                {testResult.success ? <Check size={20} /> : <AlertCircle size={20} />}
              </div>
              <div className="result-message">
                {testResult.message}
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-actions">
          <button 
            onClick={handleCloseTestModal}
            className="cancel-button"
          >
            Close
          </button>
          <button 
            onClick={handleSendTestEmail}
            className="send-button"
            disabled={!testEmailAddress || isTesting}
          >
            {isTesting ? (
              <>
                <Loader2 size={16} className="spinner" />
                Sending...
              </>
            ) : (
              <>
                <Mail size={16} />
                Send Test Email
              </>
            )}
          </button>
        </div>
      </Modal>

      <style>{`
        .smtp-configuration {
          max-width: 1000px;
          padding: 24px;
        }

        .page-header {
          margin-bottom: 32px;
        }

        .page-header h2 {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 8px 0;
        }

        .page-header p {
          color: var(--text-secondary);
          margin: 0;
        }

        .email-toggle-section {
          background: var(--bg-primary);
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-large);
          padding: 16px 24px;
          margin-bottom: 24px;
        }

        .email-toggle-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .email-toggle-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .email-toggle-label {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .email-toggle-status {
          font-size: 13px;
          font-weight: 500;
          padding: 2px 10px;
          border-radius: 12px;
        }

        .email-toggle-status.enabled {
          background: rgba(34, 197, 94, 0.15);
          color: var(--accent-green);
        }

        .email-toggle-status.disabled {
          background: rgba(239, 68, 68, 0.1);
          color: var(--accent-red);
        }

        .email-toggle-control {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .env-override-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--text-tertiary);
          font-style: italic;
        }

        .toggle-switch {
          position: relative;
          width: 48px;
          height: 26px;
          border-radius: 13px;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease;
          padding: 0;
          flex-shrink: 0;
        }

        .toggle-switch.off {
          background-color: var(--border-secondary);
        }

        .toggle-switch.on {
          background-color: var(--accent-green);
        }

        .toggle-switch.locked {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .toggle-knob {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          transition: transform 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .toggle-switch.on .toggle-knob {
          transform: translateX(22px);
        }

        .workflow-defaults-section {
          background: var(--bg-primary);
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-large);
          padding: 20px 24px;
          margin-bottom: 24px;
        }

        .workflow-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
          color: var(--text-primary);
        }

        .workflow-header svg {
          margin-top: 2px;
          flex-shrink: 0;
        }

        .workflow-header h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: var(--text-primary);
        }

        .workflow-header p {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0;
        }

        .workflow-loading {
          color: var(--text-tertiary);
          font-size: 14px;
          padding: 12px 0;
        }

        .workflow-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .workflow-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          border-radius: var(--radius-medium);
          transition: background 0.15s ease;
        }

        .workflow-item:hover {
          background: var(--bg-secondary);
        }

        .workflow-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .workflow-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .workflow-description {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .workflow-disabled-hint {
          font-size: 13px;
          color: var(--text-tertiary);
          font-style: italic;
          margin: 12px 0 0 0;
        }

        .configuration-controls {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          align-items: center;
        }

        .add-config-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--accent-red);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: var(--radius-medium);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-config-button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .save-button {
          background: var(--accent-green);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: var(--radius-medium);
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
        }

        .save-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .save-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .send-button {
          background: var(--accent-blue);
          color: white;
          border: 1px solid var(--accent-blue);
          padding: 12px 24px;
          border-radius: var(--radius-medium);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .send-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .send-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .test-modal-content {
          padding: 8px 0;
        }

        .test-modal-content p {
          color: var(--text-secondary);
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .configurations-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .loading-state, .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: var(--text-secondary);
        }

        .empty-state svg {
          color: var(--text-tertiary);
          margin-bottom: 16px;
        }

        .config-card {
          background: var(--bg-primary);
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-large);
          padding: 24px;
          transition: all 0.2s ease;
        }

        .config-card.active {
          border-color: var(--accent-green);
        }

        .config-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .config-info h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 8px 0;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .active-badge {
          background: var(--accent-green);
          color: white;
          font-size: 12px;
          padding: 4px 8px;
          border-radius: var(--radius-small);
          font-weight: 500;
        }

        .config-info p {
          color: var(--text-secondary);
          margin: 0 0 4px 0;
          font-size: 14px;
        }

        .config-details {
          font-family: monospace;
          font-size: 12px !important;
          color: var(--text-tertiary) !important;
        }

        .config-actions {
          display: flex;
          gap: 8px;
        }

        .action-button {
          width: 40px;
          height: 40px;
          border: 1px solid var(--border-secondary);
          background: var(--bg-secondary);
          border-radius: var(--radius-medium);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-button:hover {
          background: var(--border-secondary);
        }

        .action-button.activate {
          border-color: var(--accent-green);
          color: var(--accent-green);
        }

        .action-button.deactivate {
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .action-button.edit {
          border-color: var(--accent-blue);
          color: var(--accent-blue);
        }

        .action-button.delete {
          border-color: var(--accent-red);
          color: var(--accent-red);
        }

        .action-button.test {
          border-color: var(--text-primary);
          color: var(--text-primary);
        }

        .smtp-form {
          max-height: 70vh;
          overflow-y: auto;
          padding: 4px;
        }

        .form-section {
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-tertiary);
        }

        .form-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .form-section h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 16px 0;
        }

        .provider-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .provider-button {
          padding: 8px 16px;
          border: 1px solid var(--border-secondary);
          background: var(--bg-secondary);
          border-radius: var(--radius-medium);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .provider-button:hover {
          background: var(--border-secondary);
        }

        .provider-button.active {
          background: var(--accent-blue);
          border-color: var(--accent-blue);
          color: white;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.flex-2 {
          grid-column: span 2;
        }

        .form-label {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 6px;
        }

        .form-input {
          padding: 12px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .form-hint {
          font-size: 13px;
          color: var(--text-tertiary);
          margin: 8px 0 0 0;
          font-style: italic;
        }

        .provider-guide {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-left: 3px solid var(--accent-blue);
          border-radius: var(--radius-medium);
          padding: 16px 20px;
          margin-bottom: 24px;
        }

        .provider-guide h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .provider-guide ol {
          margin: 0;
          padding-left: 20px;
          font-size: 13px;
          color: var(--text-secondary);
          line-height: 1.8;
        }

        .provider-guide ol strong {
          color: var(--text-primary);
        }

        .test-result {
          padding: 12px 16px;
          border-radius: var(--radius-medium);
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
          font-weight: 500;
          margin-top: 16px;
        }

        .test-result.success {
          background: rgba(34, 197, 94, 0.1);
          color: var(--accent-green);
          border: 1px solid rgba(34, 197, 94, 0.2);
        }

        .test-result.error {
          background: rgba(239, 68, 68, 0.1);
          color: var(--accent-red);
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .result-icon {
          display: flex;
          align-items: center;
        }

        .result-message {
          flex: 1;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 24px;
          border-top: 1px solid var(--border-tertiary);
          margin-top: 24px;
        }

        .cancel-button {
          padding: 12px 20px;
          border: 1px solid var(--border-secondary);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          border-radius: var(--radius-medium);
          cursor: pointer;
        }

        .cancel-button:hover {
          background: var(--border-secondary);
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .form-group.flex-2 {
            grid-column: span 1;
          }
          
          .config-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }
          
          .configuration-controls {
            flex-direction: column;
            align-items: stretch;
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};